import { Platform, Pressable, StyleSheet, Text, View } from 'react-native'
import { toFriendlyError } from '../lib/error-message'
import { colors, fontWeight } from '../tokens'
import { trpc } from '../trpc'
import { FulfilmentConfirmBanner } from './FulfilmentConfirmBanner'
import { FulfilmentFormalAlternative } from './FulfilmentFormalAlternative'
import { FulfilmentHeader } from './FulfilmentHeader'
import { FulfilmentStepper } from './FulfilmentStepper'
import { ProposerDetailsForm } from './ProposerDetailsForm'

type Props = {
  appealId: string
  priceAmountPence: number
  receiptUrl?: string | null
  qualifiesForFormalProposal: boolean
  onCheckAnother: () => void
}

const BackArrow = () =>
  Platform.OS === 'web' ? (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5M11 18l-6-6 6-6" />
    </svg>
  ) : null

// Top-level fulfilment view: paid → (form OR stepper) → sent. Splits the
// flow into a brief precondition form ("we need your name") followed by the
// numbered stepper that makes the download → email → confirm dependency
// unmistakable.
export const FulfilmentScreen = ({
  appealId,
  priceAmountPence,
  receiptUrl,
  qualifiesForFormalProposal,
  onCheckAnother,
}: Props) => {
  const generate = trpc.appeal.generatePackage.useMutation()

  return (
    <View style={styles.wrap}>
      <Pressable accessibilityRole="link" onPress={onCheckAnother} style={styles.backLink}>
        <BackArrow />
        <Text style={styles.backText}>Back to my result</Text>
      </Pressable>

      <FulfilmentConfirmBanner
        priceAmountPence={priceAmountPence}
        receiptUrl={receiptUrl}
        variant={generate.isSuccess ? 'ready' : 'pending-details'}
      />

      {!generate.isSuccess ? (
        <ProposerDetailsForm
          qualifiesForFormalProposal={qualifiesForFormalProposal}
          submitting={generate.isPending}
          errorMessage={generate.error ? toFriendlyError(generate.error).message : undefined}
          onSubmit={(values) =>
            generate.mutate({
              appealId,
              proposerName: values.name,
              proposerEmail: values.email,
              proposerAddress: values.address,
            })
          }
        />
      ) : (
        <>
          <FulfilmentHeader />
          {generate.data.qualifiesForFormalProposal && generate.data.files.length > 0 ? (
            <FulfilmentFormalAlternative
              appealId={generate.data.appealId}
              files={generate.data.files}
            />
          ) : null}
          <FulfilmentStepper
            appealId={generate.data.appealId}
            evidenceUrl={generate.data.evidenceUrl}
            bandReviewEmail={generate.data.bandReviewEmail}
            walkthroughToolUrl={generate.data.govukWalkthrough.toolUrl}
            scotlandGuideUrl={generate.data.scotlandGuideUrl}
            onCheckAnother={onCheckAnother}
          />
        </>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  wrap: { width: '100%', maxWidth: 760, paddingVertical: 36 },
  backLink: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 22 },
  backText: { color: colors.muted, fontSize: 14, fontWeight: fontWeight.medium },
})
